import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Header } from "./components/Header";
import { Login } from "./pages/Login";
import { InvitePage } from "./pages/Invite";
import { Home } from "./pages/Home";
import { NewGame } from "./pages/NewGame";
import { GamePage } from "./pages/Game";
import { Admin } from "./pages/Admin";
import { api } from "./lib/api";
import type { OpponentUser, User } from "./lib/types";

export function App() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const location = useLocation();
  const opponentsRef = useRef<Map<number, string>>(new Map());
  const [opponentsVersion, setOpponentsVersion] = useState(0);

  const refreshOpponents = useCallback(async (): Promise<OpponentUser[]> => {
    try {
      const list = await api.listOpponents();
      const map = new Map<number, string>();
      for (const o of list) map.set(o.id, o.displayName);
      opponentsRef.current = map;
      setOpponentsVersion((v) => v + 1);
      return list;
    } catch {
      return [];
    }
  }, []);

  useEffect(() => {
    api
      .whoami()
      .then((r) => {
        setUser(r.user ?? null);
        if (r.user) void refreshOpponents();
      })
      .catch(() => setUser(null));
  }, [location.pathname, refreshOpponents]);

  if (user === undefined) {
    return <div className="page"><p className="muted">Loading…</p></div>;
  }

  // Routes that don't require auth.
  const publicRoutes = (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/invite/:token" element={<InvitePage />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );

  if (!user) return publicRoutes;

  return (
    <>
      <Header user={user} />
      <Routes>
        <Route path="/" element={<Home user={user} opponentsCache={opponentsRef.current} refreshOpponents={refreshOpponents} />} />
        <Route path="/new" element={<NewGame />} />
        <Route path="/game/:id" element={<GamePage user={user} opponentsCache={opponentsRef.current} />} />
        {user.isAdmin && <Route path="/admin" element={<Admin />} />}
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/invite/:token" element={<InvitePage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <span style={{ display: "none" }}>{opponentsVersion}</span>
    </>
  );
}
